import { useState } from 'react'
import { ChevronDownIcon, CircleHelpIcon, HandshakeIcon, RefreshCwIcon, ScaleIcon, WalletIcon } from 'lucide-react'
import type { Issue } from '@/types/risk_score'
import { RiskCard } from './RiskCard'

export const CATEGORY_ITEMS = [
  { key: 'financial' as const, label: '金銭リスク', icon: WalletIcon },
  { key: 'legal_compliance' as const, label: '法務・コンプライアンス', icon: ScaleIcon },
  { key: 'operational_continuity' as const, label: '運用継続', icon: RefreshCwIcon },
  { key: 'trust_relationship' as const, label: '信頼・関係性', icon: HandshakeIcon },
  { key: 'other' as const, label: 'その他（意味監査）', icon: CircleHelpIcon }
]

const SEVERITY_BADGE_ACTIVE = {
  critical: { background: 'var(--bp-critical-pale)', color: 'var(--bp-critical)' },
  warning: { background: 'var(--bp-warning-pale)', color: 'var(--bp-warning)' },
  info: { background: 'var(--bp-info-pale)', color: 'var(--bp-info)' }
} as const

const SEVERITY_BADGE_MUTED = { background: 'var(--bp-surface2)', color: 'var(--bp-ink3)' }

// カテゴリ（ビジネス4軸+Other）ごとに指摘をグルーピングし、積み重なった結果のナラティブを見出しに表示する。
// 開閉すると内訳（既存の RiskCard 一覧）が展開される。
// 件数バッジは常にカテゴリ全体（フィルタ前）の内訳を示し、選択中のフィルタに該当するものだけ色を付ける。
export function CategorySection ({
  item,
  issues,
  allIssues,
  activeFilter,
  summary,
  defaultOpen
}: {
  item: typeof CATEGORY_ITEMS[number]
  issues: Array<{ issue: Issue, severity: 'critical' | 'warning' | 'info' }>
  allIssues: Array<{ issue: Issue, severity: 'critical' | 'warning' | 'info' }>
  activeFilter: 'all' | 'critical' | 'warning' | 'info'
  summary: string | null | undefined
  defaultOpen: boolean
}): React.ReactElement {
  const [open, setOpen] = useState(defaultOpen)
  const Icon = item.icon
  const isEmpty = allIssues.length === 0
  const isEmptyForFilter = issues.length === 0
  const criticalCount = allIssues.filter(i => i.severity === 'critical').length
  const warningCount = allIssues.filter(i => i.severity === 'warning').length
  const infoCount = allIssues.filter(i => i.severity === 'info').length

  const badgeStyle = (severity: 'critical' | 'warning' | 'info'): { background: string, color: string } =>
    (activeFilter === 'all' || activeFilter === severity) ? SEVERITY_BADGE_ACTIVE[severity] : SEVERITY_BADGE_MUTED

  return (
    <div style={{
      background: 'var(--bp-surface)',
      border: '1px solid var(--bp-border)',
      borderRadius: 10,
      overflow: 'hidden',
      opacity: isEmpty ? 0.55 : 1
    }}
    >
      <button
        onClick={() => !isEmpty && setOpen(o => !o)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: '14px 18px',
          background: 'none',
          border: 'none',
          cursor: isEmpty ? 'default' : 'pointer',
          textAlign: 'left',
          fontFamily: 'var(--bp-font-sans)'
        }}
      >
        <Icon width={18} height={18} style={{ color: 'var(--bp-ink3)', flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: (summary != null && summary !== '') ? 5 : 0, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--bp-ink)' }}>{item.label}</span>
            {criticalCount > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, ...badgeStyle('critical') }}>
                CRITICAL {criticalCount}
              </span>
            )}
            {warningCount > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, ...badgeStyle('warning') }}>
                WARNING {warningCount}
              </span>
            )}
            {infoCount > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, ...badgeStyle('info') }}>
                INFO {infoCount}
              </span>
            )}
            {isEmpty && (
              <span style={{ fontSize: 11, color: 'var(--bp-ink3)' }}>該当なし</span>
            )}
          </div>
          {(summary != null && summary !== '') && (
            <div style={{ fontSize: 12, color: 'var(--bp-ink2)', lineHeight: 1.6 }}>{summary}</div>
          )}
        </div>
        {!isEmpty && (
          <ChevronDownIcon
            width={16}
            height={16}
            style={{
              color: 'var(--bp-ink3)',
              flexShrink: 0,
              marginTop: 2,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .15s'
            }}
          />
        )}
      </button>
      {open && !isEmpty && (
        <div style={{ padding: '0 18px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isEmptyForFilter
            ? (
              <div style={{ textAlign: 'center', padding: '10px 0', color: 'var(--bp-ink3)', fontSize: 12 }}>
                現在の表示条件に該当するリスクはありません
              </div>
              )
            : (
                issues.map(({ issue, severity }) => (
                  <RiskCard key={issue.id} issue={issue} severity={severity} />
                ))
              )}
        </div>
      )}
    </div>
  )
}
